<template>
    <AppLayout>
        <div class="max-w-7xl mx-auto px-4 py-6">
            <!-- breadcrumb -->
            <Breadcrumb
                :title="$tc('LBL_ERROR_LOG')"
                :items="[{ label: $tc('LBL_ERROR_LOG', 2) }]"
                :home-label="$t('LBL_HOME')"
                :home-url="route('landing')"
            />
            <!-- breadcrumb end -->
            <div
                class="w-full bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-overflow"
            >
                <!-- Search & Action Bar -->
                <div class="flex flex-wrap items-center justify-between gap-4 p-4 sm:flex-nowrap">
                    <!-- Search Box -->
                    <div class="relative w-full sm:w-auto">
                        <label
                            for="table-search"
                            class="sr-only"
                            >Search</label
                        >
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg
                                class="w-5 h-5 text-gray-500 dark:text-gray-400"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </div>
                        <t-input
                            id="message-search-box"
                            v-model="filters.search"
                            type="text"
                            variant="success"
                            class="w-full sm:w-80 pl-10 pr-4 py-2.5 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            placeholder="Search message..."
                        />
                    </div>

                    <!-- Action Buttons -->
                    <!-- <t-rich-select
                            v-model="filters.level"
                            class="w-60 text-sm"
                            :close-on-select="false"
                            :options="labels"
                            :clearable="true"
                            value-attribute="id"
                            text-attribute="name"
                            placeholder="Filter by label"
                        /> -->
                    <t-rich-select
                        v-model="filters.level"
                        class="w-60 text-sm"
                        :clearable="true"
                        :close-on-select="false"
                        :options="filterLabelOptions"
                        text-attribute="label"
                        value-attribute="value"
                    />

                    <t-rich-select
                        v-model="filters.user_id"
                        class="w-60 text-sm"
                        :clearable="true"
                        :close-on-select="false"
                        :options="users"
                        value-attribute="id"
                        text-attribute="name"
                        placeholder="Filter by user"
                    />
                    <input
                        type="date"
                        v-model="filters.start_date"
                        class="border rounded p-2"
                    />
                    <input
                        type="date"
                        v-model="filters.end_date"
                        class="border rounded p-2"
                    />

                    <t-button
                        @click="applyFilters"
                        class="inline-flex items-center rounded-md"
                        variant="success"
                    >
                        <span>Apply</span>
                    </t-button>
                </div>
                <div
                    class="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-none shadow-md"
                >
                    <div class="overflow-x-auto">
                        <!-- Table content to list the Products -->
                        <table
                            class="min-w-[700px] w-full table-auto text-sm sm:text-base text-left text-gray-700 dark:text-gray-300"
                        >
                            <thead class="bg-gray-100 dark:bg-gray-700 text-sm font-semibold">
                                <tr>
                                    <th class="px-4 py-3">#</th>
                                    <th class="px-4 py-3">LEVEL</th>
                                    <th class="px-4 py-3">MESSAGE</th>
                                    <th class="px-4 py-3">USER</th>
                                    <th class="px-4 py-3">CREATED AT</th>
                                    <th class="px-4 py-3">ACTIONS</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="log in logs.data"
                                    :key="log.id"
                                    class="border-t"
                                >
                                    <td class="px-4 py-3">{{ log.id }}</td>
                                    <td class="px-4 py-3">
                                        <span
                                            :class="{
                                                'bg-red-400': log.level === 'error',
                                                'bg-yellow-400': log.level === 'warning',
                                                'bg-blue-400': log.level === 'info',
                                            }"
                                            class="inline-block px-2 py-1 mt-1 text-sm text-white rounded"
                                        >
                                            {{ log.level }}
                                        </span>
                                    </td>
                                    <td class="px-4 py-3 max-w-xs truncate">{{ log.message }}</td>
                                    <td class="px-4 py-3">{{ log.user?.name || 'N/A' }}</td>
                                    <td class="px-4 py-3">{{ log.created_at | dateLabel }}</td>
                                    <td class="px-4 py-3">
                                        <button
                                            @click="viewDetails(log)"
                                            class="text-blue-600 hover:underline"
                                        >
                                            View
                                        </button>
                                    </td>
                                </tr>
                                <tr
                                    v-if="!logs.data.length"
                                    class="text-center"
                                >
                                    <td
                                        colspan="8"
                                        class="px-4 py-4 text-gray-500 dark:text-gray-400"
                                    >
                                        {{ $t('LBL_DATA_FOUND') }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div
                        v-if="logs.data.length"
                        class="flex justify-center items-center bg-gray-100 dark:bg-gray-800 px-4 py-4 border-t dark:border-gray-700"
                    >
                        <t-pagination
                            v-model="currentPage"
                            variant="rounded"
                            :total-items="totalRows"
                            :per-page="perPage"
                            :limit="limit"
                            :disabled="disabled"
                            @change="fetchPageData"
                        />
                    </div>
                    <p class="sm:hidden text-xs text-center text-gray-500 py-2">
                        ↔ Swipe left/right to scroll the table
                    </p>
                </div>
            </div>

            <!-- <div class="mt-4 flex justify-end gap-2">
                <button
                    v-if="logs.prev_page_url"
                    @click="goToPage(logs.current_page - 1)"
                    class="px-4 py-2 bg-gray-200 rounded"
                >
                    Prev
                </button>
                <span>Page {{ logs.current_page }} of {{ logs.last_page }}</span>
                <button
                    v-if="logs.next_page_url"
                    @click="goToPage(logs.current_page + 1)"
                    class="px-4 py-2 bg-gray-200 rounded"
                >
                    Next
                </button>
            </div> -->

            <!-- Modal -->
            <div
                v-if="selectedLog"
                class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            >
                <div class="bg-white w-full max-w-2xl p-6 rounded shadow-lg overflow-y-auto max-h-[90vh]">
                    <h2 class="text-xl font-bold mb-4">Log Details</h2>
                    <p><strong>ID:</strong> {{ selectedLog.id }}</p>
                    <p><strong>Level:</strong> {{ selectedLog.level }}</p>
                    <p><strong>Message:</strong> {{ selectedLog.message }}</p>
                    <p><strong>User:</strong> {{ selectedLog.user?.name || 'N/A' }}</p>
                    <p><strong>Created At:</strong> {{ selectedLog.created_at }}</p>
                    <p><strong>File:</strong> {{ selectedLog.file }}</p>
                    <p><strong>Line:</strong> {{ selectedLog.line }}</p>
                    <div class="my-2">
                        <strong>Context:</strong>
                        <pre class="bg-gray-100 p-2 rounded overflow-auto text-sm whitespace-pre-wrap">{{
                            selectedLog.context
                        }}</pre>
                    </div>
                    <div class="my-2">
                        <strong>Trace:</strong>
                        <pre class="bg-gray-100 p-2 rounded overflow-auto text-sm whitespace-pre-wrap">{{
                            selectedLog.trace
                        }}</pre>
                    </div>
                    <div class="text-right mt-4">
                        <button
                            @click="selectedLog = null"
                            class="px-4 py-2 bg-gray-300 rounded"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script>
    import { Inertia } from '@inertiajs/inertia';
    import AppLayout from '@/Layouts/AppLayout.vue';
    import Breadcrumb from '@/Components/Breadcrumb.vue';

    export default {
        name: 'ErrorLogIndex',

        components: {
            AppLayout,
            Breadcrumb,
        },

        props: {
            logs: Object,
            users: Array,
            appliedFilters: Object,
        },
        data() {
            return {
                filters: {
                    search: this.appliedFilters.search || '',
                    level: this.appliedFilters.level || '',
                    user_id: this.appliedFilters.user_id || '',
                    start_date: this.appliedFilters.start_date || '',
                    end_date: this.appliedFilters.end_date || '',
                },
                selectedLog: null,
                filterLabelOptions: [
                    { value: '', label: 'All Labels' },
                    { value: 'error', label: 'Error' },
                    { value: 'warning', label: 'Warning' },
                    { value: 'info', label: 'Info' },
                ],
                currentPage: null,
                limit: 5,
                perPage: this.logs.per_page,
                totalRows: this.logs.total,
            };
        },
        created() {
            this.currentPage = this.logs.current_page;
        },
        methods: {
            applyFilters() {
                Inertia.get(route('logs.errors.index'), this.filters, { preserveState: true });
            },
            goToPage(page) {
                Inertia.get(route('logs.errors.index'), { ...this.filters, page }, { preserveState: true });
            },
            viewDetails(log) {
                this.selectedLog = log;
            },
        },
    };
</script>
